import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { signUp, confirmSignUp, signInWithRedirect, signIn, resendSignUpCode } from 'aws-amplify/auth';
import { useAuth } from '../contexts/AuthContext';
import logger from '../services/logger';
import Canonical from './Canonical';
import OAuthButton from './OAuthButton';
import CountryAvailabilityModal from './CountryAvailabilityModal';
import Header from './home/Header';
import AuthHero from './auth/AuthHero';
import configService from '../services/configService';
import { oauthEnabled } from '../aws-config';
import '../utils/oauthConfig';
import { trackConversion } from '../hooks/usePageTracking';


function getFlagEmoji(countryCode) {
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map(char => 127397 + char.charCodeAt());
  return String.fromCodePoint(...codePoints);
}

function Signup() {
  const navigate = useNavigate();
  const { user, logout, checkAuthState } = useAuth();
  const [formData, setFormData] = useState({
    businessName: '',
    businessWebsite: '',
    email: '',
    firstName: '',
    lastName: '',
    phone: '',
    businessType: '',
    servicesOffered: '',
    businessHours: '',
    password: '',
    confirmPassword: ''
  });
  const [countryCode, setCountryCode] = useState('+1');
  const [showCountryModal, setShowCountryModal] = useState(false);
  const [confirmationCode, setConfirmationCode] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [isBetaTester, setIsBetaTester] = useState(false);
  const [resendingCode, setResendingCode] = useState(false);

  useEffect(() => {
    // Capture referral code flag from URL on component mount
    const urlParams = new URLSearchParams(window.location.search);
    const refCode = urlParams.get('ref');
    
    if (refCode) {
      console.log('🎁 Referral code captured from URL:', refCode);
      setReferralCode(refCode);
      localStorage.setItem('referralCode', refCode);
    } else {
      // No referral code - redirect to waitlist
      console.log('⚠️ No referral code found, redirecting to waitlist');
      navigate('/waitlist');
    }
    
  }, [navigate]);

  useEffect(() => {
    console.log('🔍 Signup useEffect - user state changed:', {
      hasUser: !!user,
      userEmail: user?.attributes?.email,
      showConfirmation,
      currentUrl: window.location.href
    });
    
    if (user && !showConfirmation) {
      console.log('🔍 User exists (OAuth flow), tracking referral and navigating to /plans');
      
      // Track referral signup for OAuth users
      const cognitoUserId = user.attributes?.sub;
      if (cognitoUserId) {
        trackReferralSignup(cognitoUserId);
      }
      
      // Clear any pending navigation timeout
      if (window.signupNavigationTimeout) {
        clearTimeout(window.signupNavigationTimeout);
        window.signupNavigationTimeout = null;
      }
      
      navigate('/plans');
    }
  }, [user, navigate, showConfirmation]);

  const handleLogout = async () => {
    try {
      await logout();
      setError('');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const signupWith = async (provider) => {
    try {
      console.log('Starting OAuth flow with provider:', provider);
      setLoading(true);
      await signInWithRedirect({ provider });
      console.log('signInWithRedirect called successfully');
    } catch (error) {
      console.error('OAuth error:', error);
      setError(`${provider} signup failed: ${error.message}`);
      setLoading(false);
    }
  };

  const handleSignup = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    
    console.log('🔍 handleSignup started:', {
      email: formData.email,
      hasUser: !!user,
      userEmail: user?.attributes?.email,
      showConfirmation
    });
    
    // Validate password confirmation
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }
    
    // Validate phone number format (E.164)
    const fullPhone = countryCode + formData.phone.replace(/\D/g, '');
    const phoneRegex = /^\+[1-9]\d{10,14}$/;
    if (!phoneRegex.test(fullPhone)) {
      setError('Please enter a valid phone number');
      setLoading(false);
      return;
    }
    
    try {
      console.log('🔍 Calling signUp with:', {
        username: formData.email,
        note: 'business_id will be created by backend during Step 2'
      });
      
      // Get referral code from localStorage if exists
      const refCode = localStorage.getItem('referralCode');
      
      await signUp({
        username: formData.email,
        password: formData.password,
        options: {
          userAttributes: {
            email: formData.email,
            name: `${formData.firstName} ${formData.lastName}`,
            phone_number: fullPhone,
            'custom:business_type': formData.businessType,
            'custom:country_code': countryCode,
            'custom:user_type': 'customers',  // Resellers go directly to customers group
            ...(refCode && { 'custom:reseller_id': refCode })  // Add reseller_id if exists
          }
        }
      });
      
      console.log('🔍 signUp successful, showing confirmation');
      setShowConfirmation(true);
    } catch (error) {
      console.log('🔍 signUp error:', {
        name: error.name,
        message: error.message,
        code: error.code
      });
      
      // If user already exists but isn't confirmed, show confirmation screen
      if (error.message.includes('User already exists') || error.name === 'UsernameExistsException') {
        console.log('🔍 User exists but unconfirmed, showing confirmation');
        setShowConfirmation(true);
        setError('Account exists but needs confirmation. Please check your email.');
      } else {
        setError(error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmation = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    
    console.log('🔍 handleConfirmation started:', {
      email: formData.email,
      confirmationCode,
      hasUser: !!user,
      userEmail: user?.attributes?.email
    });
    
    try {
      // Sign out any existing user first
      if (user) {
        console.log('🔍 Signing out existing user before confirmation');
        await logout();
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      
      console.log('🔍 Calling confirmSignUp');
      await confirmSignUp({
        username: formData.email,
        confirmationCode
      });
      
      console.log('🔍 confirmSignUp successful, signing in');
      
      // Automatically sign in after confirmation
      const result = await signIn({
        username: formData.email,
        password: formData.password
      });
      
      console.log('🔍 signIn successful:', {
        userId: result?.userId,
        isSignedIn: result?.isSignedIn
      });
      
      // Log user login after successful confirmation and sign in
      await logger.logUserLogin(result);
      
      // Wait for auth state to update and get the cognito user ID
      await new Promise(resolve => setTimeout(resolve, 1000));
      await checkAuthState();
      
      // Track referral signup if referral code exists
      let cognitoUserId = result?.userId;
      if (!cognitoUserId) {
        try {
          const lastAuthUser = localStorage.getItem('CognitoIdentityServiceProvider.7123hmqco0fqs1j2hfd0kau4hs.LastAuthUser');
          if (lastAuthUser) {
            const idTokenKey = `CognitoIdentityServiceProvider.7123hmqco0fqs1j2hfd0kau4hs.${lastAuthUser}.idToken`;
            const idToken = localStorage.getItem(idTokenKey);
            if (idToken) {
              const payload = JSON.parse(atob(idToken.split('.')[1]));
              cognitoUserId = payload.sub;
            }
          }
        } catch (error) {
          console.error('Failed to extract cognito user ID:', error);
        }
      }
      
      console.log('🎁 Tracking referral with cognito user ID:', cognitoUserId);
      await trackReferralSignup(cognitoUserId);
      
      // Create waitlist record with full signup data
      if (cognitoUserId) {
        try {
          const fullPhone = countryCode + formData.phone.replace(/\D/g, '');
          await configService.createWaitlistRecord({
            businessName: formData.businessName,
            businessWebsite: formData.businessWebsite,
            email: formData.email,
            firstName: formData.firstName,
            lastName: formData.lastName,
            phone: fullPhone,
            jobRole: 'Owner', // Default for signup flow
            referralSource: referralCode ? 'referral' : 'direct',
            businessType: formData.businessType,
            industry: formData.businessType, // Use same as business type
            servicesOffered: formData.servicesOffered,
            businessHours: formData.businessHours,
            monthlyCallVolume: '0-100' // Default for signup flow
          });
          console.log('✅ Created waitlist record');
        } catch (waitlistError) {
          console.warn('⚠️ Failed to create waitlist record:', waitlistError);
          // Don't fail signup if waitlist creation fails
        }
      }
      
      console.log('🔍 Manually refreshing auth state before navigation');
      // Wait a moment for tokens to be stored, then refresh auth state
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Manually trigger auth state check
      await checkAuthState();
      
      // The useAuth context should automatically detect the change and navigate
      // But if it doesn't work within 2 seconds, navigate manually
      const navigationTimeout = setTimeout(() => {
        console.log('🔍 Navigation timeout - forcing navigation to /plans');
        navigate('/plans');
      }, 2000);
      
      // Clear timeout if user state updates (handled by useEffect)
      window.signupNavigationTimeout = navigationTimeout;
      
    } catch (error) {
      console.log('🔍 handleConfirmation error:', {
        name: error.name,
        message: error.message,
        code: error.code
      });
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setResendingCode(true);
    setError('');
    
    try {
      await resendSignUpCode({
        username: formData.email
      });
      setError('New confirmation code sent to your email.');
    } catch (error) {
      console.error('Resend code error:', error);
      setError(`Failed to resend code: ${error.message}`);
    } finally {
      setResendingCode(false);
    }
  };

  const trackReferralSignup = async (cognitoUserId) => {
    const refCode = localStorage.getItem('referralCode');
    if (refCode && cognitoUserId) {
      try {
        console.log('🎁 Tracking referral signup:', { refCode, cognitoUserId });
        await configService.trackReferralSignup(refCode, cognitoUserId);
        console.log('✅ Referral signup tracked successfully');
        trackConversion('signup_complete', '/signup');
        // Note: reseller_id will be assigned in Plans.js before subscription
      } catch (error) {
        console.error('❌ Failed to track referral:', error);
        // Don't throw error - referral tracking failure shouldn't block signup
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Apply masking for phone number if country code is +1
    if (name === 'phone' && countryCode === '+1') {
      const digits = value.replace(/\D/g, '');
      let formatted = digits;
      
      if (digits.length >= 6) {
        formatted = `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
      } else if (digits.length >= 3) {
        formatted = `${digits.slice(0, 3)}-${digits.slice(3)}`;
      }
      
      setFormData({ ...formData, [name]: formatted });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Canonical path="/signup" />
      <Header />
      <div className="flex flex-1">
        <AuthHero />
        
        <div className="w-full lg:w-2/3 flex justify-center items-center px-8 py-12 bg-[#f9f9fb]">
        <div className="bg-white p-10 rounded-xl border border-gray-200 w-full max-w-xl">
          {referralCode && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
              <p className="text-green-800 text-sm font-medium">
                🎉 Referral code applied! Skip the waitlist and get started today.
              </p>
            </div>
          )}
          <h2 className="text-2xl font-semibold text-gray-900 mb-2 font-['Inter']">Create Your Account</h2>
          <p className="text-gray-600 mb-6 text-sm font-['Inter']">Get started with your AI assistant today.</p>

          {error && (
            <div className="bg-red-50 text-red-700 p-3 rounded-lg mb-4 text-sm border border-red-200">
              {error}
            </div>
          )}

          {!showConfirmation ? (
            <>
              {oauthEnabled && (
                <>
                  <div className="mb-6 space-y-2">
                    <OAuthButton 
                      provider="google"
                      onClick={() => signupWith('Google')}
                      className={loading ? 'opacity-70 cursor-not-allowed' : ''}
                    />
                    <OAuthButton 
                      provider="facebook"
                      onClick={() => signupWith('Facebook')}
                      className={loading ? 'opacity-70 cursor-not-allowed' : ''}
                    />
                    <OAuthButton 
                      provider="apple"
                      onClick={() => signupWith('SignInWithApple')}
                      className={loading ? 'opacity-70 cursor-not-allowed' : ''}
                    />
                    <OAuthButton 
                      provider="microsoft"
                      onClick={() => signupWith('LoginWithAmazon')}
                      className={loading ? 'opacity-70 cursor-not-allowed' : ''}
                    />
                  </div>

                  <div className="my-6 text-gray-500 relative text-sm text-center">
                    <div className="absolute top-1/2 left-0 right-0 h-px bg-gray-200"></div>
                    <span className="bg-white px-4 relative">or</span>
                  </div>
                </>
              )}

              <form onSubmit={handleSignup} className="flex flex-col gap-3">
                <input 
                  type="text" 
                  name="businessName"
                  placeholder="Business Name" 
                  value={formData.businessName}
                  onChange={handleInputChange}
                  required 
                  className="p-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <input 
                  type="url" 
                  name="businessWebsite"
                  placeholder="Business Website (optional - e.g., example.com)" 
                  value={formData.businessWebsite}
                  onChange={handleInputChange}
                  className="p-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <input 
                  type="email" 
                  name="email"
                  placeholder="Business Email" 
                  value={formData.email}
                  onChange={handleInputChange}
                  required 
                  className="p-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <div className="flex gap-2 w-full">
                  <input 
                    type="text" 
                    name="firstName"
                    placeholder="First Name" 
                    value={formData.firstName}
                    onChange={handleInputChange}
                    required 
                    className="p-3 border border-gray-300 rounded-lg text-sm w-1/2 min-w-0 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <input 
                    type="text" 
                    name="lastName"
                    placeholder="Last Name" 
                    value={formData.lastName}
                    onChange={handleInputChange}
                    required 
                    className="p-3 border border-gray-300 rounded-lg text-sm w-1/2 min-w-0 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div className="flex gap-2">
                  <select 
                    value={countryCode}
                    onChange={(e) => setCountryCode(e.target.value)}
                    className="p-3 border border-gray-300 rounded-lg text-sm w-20 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="+1">{getFlagEmoji('US')} +1</option>
                    <option value="+1">{getFlagEmoji('CA')} +1</option>
                    <option value="+44">{getFlagEmoji('GB')} +44</option>
                    <option value="+33">{getFlagEmoji('FR')} +33</option>
                    <option value="+49">{getFlagEmoji('DE')} +49</option>
                    <option value="+39">{getFlagEmoji('IT')} +39</option>
                    <option value="+34">{getFlagEmoji('ES')} +34</option>
                    <option value="+61">{getFlagEmoji('AU')} +61</option>
                    <option value="+81">{getFlagEmoji('JP')} +81</option>
                  </select>
                  <input 
                    type="tel" 
                    name="phone"
                    placeholder="Phone Number" 
                    value={formData.phone}
                    onChange={handleInputChange}
                    required 
                    className="p-3 border border-gray-300 rounded-lg text-sm flex-1 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <select 
                  name="businessType"
                  value={formData.businessType}
                  onChange={handleInputChange}
                  required 
                  className="p-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">What type of business?</option>
                  <option value="auto">Auto Service</option>
                  <option value="dental">Dental Practice</option>
                  <option value="government">Government</option>
                  <option value="home">Home Services</option>
                  <option value="medical">Medical Practice</option>
                  <option value="professional">Professional Services</option>                  
                  <option value="personal-trainer">Personal Trainer</option>                
                  <option value="restaurant">Restaurant / Food Service</option>
                  <option value="retail">Retail / E-commerce</option>
                  <option value="salon">Hair Salon / Barbershop</option>
                  <option value="spa">Spa / Wellness</option>
                  <option value="trades">Trades</option>
                  <option value="other">Other</option>
                </select>
                <textarea 
                  name="servicesOffered"
                  placeholder="What services does your business offer? (e.g., haircuts, dental cleanings, car repairs)" 
                  value={formData.servicesOffered}
                  onChange={handleInputChange}
                  required 
                  className="p-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-h-[80px] resize-none"
                />
                <input 
                  type="text" 
                  name="businessHours"
                  placeholder="Business Hours (e.g., Mon-Fri 9AM-6PM, Sat 10AM-4PM)" 
                  value={formData.businessHours}
                  onChange={handleInputChange}
                  required 
                  className="p-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <input 
                  type="password" 
                  name="password"
                  placeholder="Create Password (min 8 characters)" 
                  value={formData.password}
                  onChange={handleInputChange}
                  required 
                  minLength="8"
                  className="p-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <input 
                  type="password" 
                  name="confirmPassword"
                  placeholder="Confirm Password" 
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  required 
                  minLength="8"
                  className="p-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <button 
                  type="submit" 
                  disabled={loading}
                  className={`btn-primary text-sm py-3 ${
                    loading ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer'
                  }`}
                >
                  {loading ? 'Creating Account...' : 'Get Started Now'}
                </button>
              </form>
            </>
          ) : (
            <form onSubmit={handleConfirmation} className="flex flex-col gap-3">
              <h2 className="text-xl font-semibold text-gray-900 mb-2 font-['Inter']">Check Your Email</h2>
              <p className="text-gray-600 mb-3 text-sm">
                We sent a confirmation code to {formData.email}
              </p>
              <input 
                type="text" 
                placeholder="Enter confirmation code" 
                value={confirmationCode}
                onChange={(e) => setConfirmationCode(e.target.value)}
                required 
                className="p-3 border border-gray-300 rounded-lg text-sm text-center focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <button 
                type="submit" 
                disabled={loading}
                className={`btn-primary text-sm py-3 ${
                  loading ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer'
                }`}
              >
                {loading ? 'Confirming...' : 'Confirm Account'}
              </button>
              
              {error && error.toLowerCase().includes('invalid code') && (
                <p className="mt-3 text-xs text-gray-600">
                  <button 
                    type="button"
                    onClick={handleResendCode}
                    disabled={resendingCode}
                    className="text-blue-600 hover:text-blue-700 font-medium bg-transparent border-none cursor-pointer underline text-xs"
                  >
                    {resendingCode ? 'Sending...' : 'Request a new code'}
                  </button>
                </p>
              )}
            </form>
          )}

          {!showConfirmation && (
            <p className="mt-4 text-xs text-gray-600">
              Already have an account?{' '}
              <Link to="/login" className="text-blue-600 hover:text-blue-700 font-medium no-underline">
                Sign in here
              </Link>
              {' '}or{' '}
              <Link to="/waitlist" className="text-blue-600 hover:text-blue-700 font-medium no-underline">
                join the waitlist
              </Link>
            </p>
          )}
        </div>
      </div>
      </div>
      
      <CountryAvailabilityModal 
        isOpen={showCountryModal}
        onClose={() => setShowCountryModal(false)}
        countryCode={countryCode}
        email={formData.email}
      />
    </div>
  );
}

export default Signup;